<script setup lang="ts">
import type { IntervalInputProps } from '.';

import { cn } from '@/utils';
import { useId } from 'vue';

import IconArrowLeftRight from '~icons/mdi/arrow-left-right';

import CurrencyInput from '../CurrencyInput/Input.vue';
import RangeFilterActions from '../Filters/RangeFilterActions/Actions.vue';
import {
	intervalInputClassesVariants,
	intervalInputIconVariants,
	intervalInputLabelVariants,
	intervalInputSuffixVariants,
	intervalInputTitleVariants
} from './variants';

const props = withDefaults(defineProps<IntervalInputProps>(), {
	updateOn: 'input',
	variant: 'primary',
	showActions: false,
	closeOnApply: false
});

const emit = defineEmits<{
	apply: [focusNext?: boolean];
	clear: [];
	'update:range': [
		min: number | null | undefined,
		max: number | null | undefined
	];
}>();

const firstInputId = useId();
const secondInputId = useId();

const localMin = ref<number | null | undefined>(undefined);
const localMax = ref<number | null | undefined>(undefined);
const lastAppliedMin = ref<number | null | undefined>(undefined);
const lastAppliedMax = ref<number | null | undefined>(undefined);
const lastEditedKey = ref<'min' | 'max' | null>(null);
const rangeFilterActionsRef = ref<InstanceType<
	typeof RangeFilterActions
> | null>(null);

const min = defineModel<number | null | undefined>('min', {
	default: () => null
});
const max = defineModel<number | null | undefined>('max', {
	default: () => null
});

const unit = computed(() => (props.unit === 'area' ? 'm²' : 'R$'));

const unitPosition = computed(() => (props.unit === 'area' ? 'right' : 'left'));

const hasLocalValue = computed(
	() => localMin.value != null || localMax.value != null
);

function validateAndUpdate(key: 'min' | 'max') {
	const minVal = localMin.value;
	const maxVal = localMax.value;

	if (minVal == null || maxVal == null) return;

	if (key === 'min') {
		if (minVal > maxVal) {
			localMin.value = null;
		}
	}

	if (key === 'max') {
		if (maxVal < minVal) {
			localMax.value = null;
		}
	}
}

function validatePendingValues() {
	const minVal = localMin.value;
	const maxVal = localMax.value;

	if (minVal == null || maxVal == null || minVal <= maxVal) return;

	const minChanged = !Object.is(lastAppliedMin.value, minVal);
	const maxChanged = !Object.is(lastAppliedMax.value, maxVal);
	const invalidKey =
		minChanged !== maxChanged
			? minChanged
				? 'min'
				: 'max'
			: (lastEditedKey.value ?? 'max');

	if (invalidKey === 'min') {
		localMin.value = null;
		return;
	}

	localMax.value = null;
}

function handleModelUpdate(key: 'min' | 'max') {
	lastEditedKey.value = key;
}

function handleInput(key: 'min' | 'max') {
	if (props.updateOn !== 'input') return;

	applyValue(key);
}

function applyValue(key: 'min' | 'max') {
	validateAndUpdate(key);

	if (key === 'min') {
		min.value = localMin.value;
	}

	if (key === 'max') {
		max.value = localMax.value;
	}
}

function applyPendingValues() {
	validatePendingValues();

	const didRangeChange =
		!Object.is(lastAppliedMin.value, localMin.value) ||
		!Object.is(lastAppliedMax.value, localMax.value);

	if (!didRangeChange) return;

	lastAppliedMin.value = localMin.value;
	lastAppliedMax.value = localMax.value;
	lastEditedKey.value = null;
	emit('update:range', localMin.value, localMax.value);
}

function handleBlur() {
	if (props.showActions) return;

	if (min.value !== localMin.value) applyValue('min');
	if (max.value !== localMax.value) applyValue('max');
}

function applyValues(focusNext = false) {
	applyPendingValues();
	if (focusNext) {
		emit('apply', true);
		return;
	}

	emit('apply');
}

function clearValues() {
	localMin.value = null;
	localMax.value = null;
	lastAppliedMin.value = null;
	lastAppliedMax.value = null;
	lastEditedKey.value = null;
	emit('clear');
}

// O DropdownMenuContent (reka-ui) bloqueia o Tab por padrão para qualquer coisa
// dentro dele (focus trap), então o navegador nunca move o foco pro segundo
// campo sozinho — por isso apliquei e movi o foco manualmente aqui.
function handleFirstInputKeydown(event: KeyboardEvent) {
	if (event.key === 'Enter') {
		event.preventDefault();
		event.stopPropagation();
		if (!props.showActions) applyValue('min');
		return;
	}

	if (event.key === 'Tab' && !event.shiftKey) {
		event.preventDefault();
		event.stopPropagation();
		if (!props.showActions) applyValue('min');
		document.getElementById(secondInputId)?.focus();
	}
}

function handleSecondInputKeydown(event: KeyboardEvent) {
	if (event.key === 'Tab' && !event.shiftKey && props.showActions) {
		event.preventDefault();
		event.stopPropagation();
		rangeFilterActionsRef.value?.focusFirstAction();
		return;
	}

	if (event.key !== 'Enter') return;

	event.preventDefault();
	event.stopPropagation();
	if (!props.showActions) applyValue('max');
}

function focusSecondInput() {
	document.getElementById(secondInputId)?.focus();
}

// Usado só pelos filtros "abertos" (Preço/Área), que ficam dentro de um
// DropdownMenu (reka-ui): o DropdownMenuContent foca a si mesmo (não o
// primeiro campo) quando abre, e esse auto-focus roda de forma assíncrona,
// "ganhando a corrida" de qualquer coisa que a gente faça num nextTick
// simples — por isso esperamos o wrapper do dropdown (ancestral direto do
// nosso próprio campo, achado via closest, nunca uma busca global) realmente
// receber o foco antes de redirecionar pro campo mínimo.
function focusFirstInputWhenReady() {
	const firstInput = document.getElementById(firstInputId);

	if (!firstInput) return;

	const dropdownContent = firstInput.closest<HTMLElement>('[role="menu"]');

	if (!dropdownContent) {
		firstInput.focus();
		return;
	}

	if (document.activeElement === dropdownContent) {
		firstInput.focus();
	} else {
		dropdownContent.addEventListener('focus', () => firstInput.focus(), {
			once: true
		});
	}
}

defineExpose({ focusFirstInputWhenReady });

watch(
	min,
	newMin => {
		lastAppliedMin.value = newMin;
		localMin.value = newMin;
	},
	{ immediate: true }
);

watch(
	max,
	newMax => {
		lastAppliedMax.value = newMax;
		localMax.value = newMax;
	},
	{ immediate: true }
);
</script>

<template>
	<div :class="cn('flex w-full flex-col gap-5', props.class)">
		<h4 :class="intervalInputTitleVariants({ variant: props.variant })">
			{{ title }}
		</h4>
		<form class="flex w-full items-center gap-2">
			<CurrencyInput
				:id="firstInputId"
				v-model="localMin"
				:placeholder="minLabel"
				:error="error"
				:affix="unit"
				:unit-position="unitPosition"
				:unit="unit"
				tabindex="0"
				class="flex-1"
				:class="
					cn(
						intervalInputClassesVariants({ variant: props.variant }),
						props.inputClasses
					)
				"
				:affix-class="intervalInputSuffixVariants({ variant: props.variant })"
				:label-class="intervalInputLabelVariants({ variant: props.variant })"
				@change="handleInput('min')"
				@update:model-value="handleModelUpdate('min')"
				@blur="handleBlur"
				@keydown="handleFirstInputKeydown"
			/>
			<IconArrowLeftRight
				:class="intervalInputIconVariants({ variant: props.variant })"
			/>
			<CurrencyInput
				:id="secondInputId"
				v-model="localMax"
				:placeholder="maxLabel"
				:error="error"
				:affix="unit"
				:unit-position="unitPosition"
				:unit="unit"
				tabindex="0"
				class="flex-1"
				:class="
					cn(
						intervalInputClassesVariants({ variant: props.variant }),
						props.inputClasses
					)
				"
				:affix-class="intervalInputSuffixVariants({ variant: props.variant })"
				:label-class="intervalInputLabelVariants({ variant: props.variant })"
				@change="handleInput('max')"
				@update:model-value="handleModelUpdate('max')"
				@blur="handleBlur"
				@keydown="handleSecondInputKeydown"
			/>
		</form>
		<RangeFilterActions
			v-if="props.showActions && props.filterName"
			ref="rangeFilterActionsRef"
			:filter-name="props.filterName"
			:has-value="hasLocalValue"
			:variant="props.variant"
			:close-on-apply="props.closeOnApply"
			@apply="applyValues"
			@clear="clearValues"
			@focus-previous="focusSecondInput"
		/>
	</div>
</template>
